import { lazy, Suspense, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import logo from '../../traderhub24-logo.svg'
import { useAuth } from '../contexts/AuthContext'
import AdminLogin from './AdminLogin'
import { AdminModuleLoading } from './adminModuleShared'
import '../app-test.css'

const UserManagement = lazy(() => import('../UserManagement'))
const SymbolManagement = lazy(() => import('../SymbolManagement'))
const SupportEmailManagement = lazy(() => import('../SupportEmailManagement'))
const BulkEmailManagement = lazy(() => import('../BulkEmailManagement'))
const MembershipLimitsManagement = lazy(() => import('../MembershipLimitsManagement'))

type AdminTab = 'users' | 'symbols' | 'support' | 'bulk-email' | 'limits'

const TAB_META: Record<AdminTab, { title: string; subtitle: string }> = {
  users: { title: 'User management', subtitle: 'Accounts, membership, and billing details.' },
  symbols: { title: 'Symbol management', subtitle: 'Market symbols and sync operations.' },
  support: { title: 'Support inbox', subtitle: 'Contact messages and replies.' },
  'bulk-email': { title: 'Bulk email', subtitle: 'Campaigns and announcements.' },
  limits: { title: 'Membership limits', subtitle: 'Plan quotas and feature caps.' },
}

export default function AdminApp() {
  const { t } = useTranslation(['common', 'pages'])
  const { user, userProfile, loading: authLoading, profileLoading, signOut } = useAuth()
  const [activeTab, setActiveTab] = useState<AdminTab>('users')
  const [mobileNav, setMobileNav] = useState(false)

  const displayName = user?.email?.split('@')[0] ?? 'Admin'
  const initials = displayName.slice(0, 2).toUpperCase()
  const meta = TAB_META[activeTab]

  const navItems = useMemo(
    () => [
      { id: 'users' as AdminTab, label: t('common:nav.userManagement') || 'Users' },
      { id: 'symbols' as AdminTab, label: t('common:nav.symbolManagement') || 'Symbols' },
      { id: 'support' as AdminTab, label: t('common:nav.supportEmail') || 'Support' },
      { id: 'bulk-email' as AdminTab, label: t('common:nav.bulkEmail') || 'Bulk email' },
      { id: 'limits' as AdminTab, label: t('common:nav.membershipLimits') || 'Limits' },
    ],
    [t],
  )

  if (authLoading || (user && profileLoading)) {
    return <div className="app-test-status">Loading admin…</div>
  }

  if (!user) {
    return <AdminLogin />
  }

  if (!userProfile?.is_admin) {
    return (
      <div className="app-test at-admin-login">
        <div className="at-backdrop-orb at-orb-one" aria-hidden />
        <div className="at-backdrop-orb at-orb-two" aria-hidden />
        <div className="at-admin-login-card at-admin-denied">
          <h1>Access denied</h1>
          <p>This account does not have administrator privileges.</p>
          <button type="button" className="at-export" onClick={() => signOut()}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  const tabContent = (
    <Suspense fallback={<AdminModuleLoading />}>
      {activeTab === 'users' && <UserManagement />}
      {activeTab === 'symbols' && <SymbolManagement />}
      {activeTab === 'support' && <SupportEmailManagement />}
      {activeTab === 'bulk-email' && <BulkEmailManagement />}
      {activeTab === 'limits' && <MembershipLimitsManagement />}
    </Suspense>
  )

  return (
    <div className="app-test at-admin">
      <div className="at-backdrop-orb at-orb-one" aria-hidden />
      <div className="at-backdrop-orb at-orb-two" aria-hidden />
      <div className="at-shell">
        {mobileNav && (
          <button
            type="button"
            className="at-mobile-scrim"
            aria-label="Close navigation"
            onClick={() => setMobileNav(false)}
          />
        )}
        <aside className={`at-sidebar${mobileNav ? ' is-open' : ''}`}>
          <div className="at-brand">
            <img src={logo} alt="TraderHub24" />
          </div>
          <nav className="at-nav" aria-label="Admin">
            <div className="at-nav-section">
              <p>Administration</p>
              {navItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={activeTab === item.id ? 'active' : undefined}
                  onClick={() => {
                    setActiveTab(item.id)
                    setMobileNav(false)
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>
          <div className="at-sidebar-user">
            <div className="at-sidebar-user-info">
              <span className="at-avatar">{initials}</span>
              <span>
                <strong>{displayName}</strong>
                <small>{user.email}</small>
              </span>
            </div>
            <button type="button" className="at-admin-signout" onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        </aside>

        <main className="at-main at-admin-main">
          <header className="at-header">
            <button
              type="button"
              className="at-icon-button at-menu-button"
              aria-label="Menu"
              onClick={() => setMobileNav((open) => !open)}
            >
              ☰
            </button>
            <div className="at-heading">
              <h1>{meta.title}</h1>
              <p>{meta.subtitle}</p>
            </div>
          </header>
          <div className="at-admin-body">{tabContent}</div>
        </main>
      </div>
    </div>
  )
}
